import { useEffect, useState, type FormEvent } from 'react';
import type { GuestInvitation } from '@jabberr/core';
import { api, errorMessage } from '../../lib/api.js';
import { ErrorBox } from '../ui.js';
import '../../styles/guest-invite.css';

const statusLabel = (row: GuestInvitation): string =>
  row.status === 'accepted' ? 'Joined' : row.status === 'revoked' ? 'Cancelled' : row.waiting ? 'Waiting on John/Adam' : row.deliveryStatus === 'failed' ? 'Email failed' : 'Invited';

const ROLES: Array<{ id: GuestInvitation['role']; label: string }> = [
  { id: 'client_contributor', label: 'Client contributor' },
  { id: 'client_lead', label: 'Client lead' },
  { id: 'client_trusted', label: 'Client trusted' },
];

/**
 * A project Owner (or Admin) invites a client guest with a client role (restored from codex/source-reconciliation-wip,
 * on Yard's access, ADR 0022). Yard holds the request until John or Adam approves; then the email link goes out.
 */
export function GuestInvitationPanel({ projectId }: { projectId: string }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<GuestInvitation['role']>('client_contributor');
  const [rows, setRows] = useState<GuestInvitation[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = async () => setRows((await api.guestInvitations.list(projectId)).invitations);
  useEffect(() => { void refresh().catch((err) => setError(errorMessage(err))); }, [projectId]);
  const send = async (recipient: { name: string; email: string; role: GuestInvitation['role'] }) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { invitation } = await api.guestInvitations.send(projectId, recipient);
      setNotice(invitation.waiting
        ? `Waiting on John/Adam to approve ${invitation.email}. The email goes out once they do.`
        : invitation.deliveryStatus === 'sent'
          ? `Invitation sent to ${invitation.email}. The link works once, for 24 hours.`
          : 'The invitation is saved, but the email could not be sent. Try Send again.');
      setName(''); setEmail('');
      await refresh();
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const revoke = async (row: GuestInvitation) => {
    setBusy(true); setError(null);
    try { await api.guestInvitations.revoke(projectId, row.id); await refresh(); setNotice(`The invitation to ${row.email} is cancelled.`); }
    catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const submit = (e: FormEvent) => { e.preventDefault(); void send({ name: name.trim(), email: email.trim(), role }); };
  return (
    <section className="guest-invite" aria-labelledby="guest-invite-h">
      <div className="section-head"><h2 id="guest-invite-h">Invite a client guest</h2></div>
      <p className="muted small">The guest gets an email link to this project only: their own conversations, no personal space, nothing team-wide. John or Adam approves each new person in Loop first. To remove someone who joined, use People in Loop.</p>
      <form className="guest-invite-form" onSubmit={submit}>
        <label className="field"><span className="field-label">Name</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required /></label>
        <label className="field"><span className="field-label">Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} required /></label>
        <label className="field"><span className="field-label">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as GuestInvitation['role'])}>
            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim() || !email.trim()}>Send invitation</button>
      </form>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {notice ? <p role="status" className="small">{notice}</p> : null}
      {rows.length ? (
        <ul className="guest-invite-list">
          {rows.map((row) => (
            <li key={row.id}>
              <span className="guest-invite-who"><strong>{row.name}</strong> <span className="muted">{row.email}</span></span>
              <span className="muted small">{ROLES.find((r) => r.id === row.role)?.label}</span>
              <span className={`guest-invite-status guest-invite-${row.waiting ? 'waiting' : row.status}`}>{statusLabel(row)}</span>
              {row.status === 'pending' ? (
                <span className="guest-invite-actions">
                  {!row.waiting ? <button type="button" className="link-btn small" disabled={busy} onClick={() => void send(row)}>Send again</button> : null}
                  <button type="button" className="link-btn small" disabled={busy} onClick={() => void revoke(row)}>Cancel</button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
